import React, { useEffect, useState } from "react";
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, ExternalLink, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { AsanaProjectsPanel } from "./AsanaProjectsPanel";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB") : "—");

const LinkPicker = ({ item, onLinked }) => {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 3) { setHits([]); return; }
    const t = setTimeout(() => api.get("/properties", { params: { search: q.trim(), page_size: 8 } })
      .then(({ data }) => setHits(data.items || [])).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);
  const link = async (p) => {
    setBusy(true);
    try {
      await api.post("/asana/manual-link", { task_gid: item.task_gid, property_id: p.id });
      toast.success("Linked to property", { description: p.address });
      onLinked();
    } catch (e) {
      toast.error("Link failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-1.5 w-full" data-testid={`asana-link-picker-${item.task_gid}`}>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search address, postcode or UPRN…"
        className="w-full border border-slate-300 px-2 py-1 text-xs" data-testid="asana-link-search-input" />
      {busy && <div className="text-xs text-muted-foreground mt-1"><Loader2 className="inline h-3 w-3 animate-spin mr-1" />Linking & re-syncing…</div>}
      {!busy && hits.map((p) => (
        <button key={p.id} type="button" onClick={() => link(p)} className="block w-full text-left px-2 py-1 text-xs hover:bg-slate-100"
          data-testid={`asana-link-option-${p.id}`}>{p.address} · {p.postcode}</button>
      ))}
    </div>
  );
};

const UnmatchedRow = ({ u, isAdmin, onLinked }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="px-3 py-1.5 text-xs border-b border-slate-100 last:border-0" data-testid={`asana-unmatched-row-${u.task_gid || u.task}`}>
      <div className="flex items-center justify-between gap-2">
        <span><span className="font-mono text-slate-500 mr-2">{u.project}</span>{u.address || u.task}</span>
        <span className="flex items-center gap-2 shrink-0">
          {isAdmin && u.task_gid && (
            <button type="button" onClick={() => setOpen(!open)} className="text-blue-700 flex items-center gap-1" data-testid={`asana-link-btn-${u.task_gid}`}>
              <Link2 className="h-3.5 w-3.5" />{open ? "Cancel" : "Link"}
            </button>
          )}
          {u.url && <a href={u.url} target="_blank" rel="noreferrer" className="text-blue-700"><ExternalLink className="h-3.5 w-3.5" /></a>}
        </span>
      </div>
      {open && <LinkPicker item={u} onLinked={() => { setOpen(false); onLinked(); }} />}
    </div>
  );
};

const UnmatchedList = ({ items, isAdmin, onLinked }) => (
  <div className="mt-3 border border-slate-200 max-h-72 overflow-y-auto" data-testid="asana-unmatched-list">
    {items.map((u, i) => <UnmatchedRow key={u.task_gid || i} u={u} isAdmin={isAdmin} onLinked={onLinked} />)}
  </div>
);

export const AsanaSyncCard = ({ isAdmin }) => {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = () => api.get("/asana/status").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/asana/sync");
      toast.success(`Asana synced · ${data.properties_linked} properties linked`, { description: `${data.unmatched_count} tasks couldn't be matched` });
      load();
    } catch (e) {
      toast.error("Asana sync failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSyncing(false);
    }
  };

  if (!status) return null;
  const last = status.last_sync;
  return (
    <Card className="rounded-none border-border p-6" data-testid="asana-sync-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Asana projects</div>
          <div className="flex items-center gap-2 text-sm font-semibold" data-testid="asana-configured-state">
            {status.configured
              ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Connected · syncs daily at 01:30 UTC</>
              : <><AlertTriangle className="h-4 w-4 text-amber-600" /> Not connected — {status.has_token ? "add at least one project below" : "Asana token not added yet"}</>}
          </div>
        </div>
        {isAdmin && (
          <Button onClick={syncNow} disabled={!status.configured || syncing} className="rounded-none text-white" style={{ backgroundColor: "var(--hi-olive)" }} data-testid="asana-sync-now-btn">
            {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Sync now
          </Button>
        )}
      </div>
      <AsanaProjectsPanel projects={status.projects} cohorts={status.cohorts} isAdmin={isAdmin} onChange={load} />
      {last && (
        <div className="mt-4 text-sm" data-testid="asana-last-sync">
          {last.ok === false && <div className="text-red-700 text-xs mb-2">Last run failed: {last.error}</div>}
          <div className="text-xs text-muted-foreground">Last sync {fmt(last.finished_at)}</div>
          {(last.projects || []).map((p) => (
            <div key={p.gid} className="font-mono text-xs mt-1">{p.name}: {p.matched}/{p.tasks} tasks matched</div>
          ))}
          {last.unmatched_count > 0 && (
            <>
              <div className="text-xs font-semibold mt-3">{last.unmatched_count} Asana task(s) couldn't be matched to a property</div>
              <div className="text-xs text-muted-foreground">Most are templates/admin tasks or homes not in your portfolio. If one is a real property, click Link to attach it — it then stays off the target lists for good.</div>
              <UnmatchedList items={last.unmatched || []} isAdmin={isAdmin} onLinked={load} />
            </>
          )}
        </div>
      )}
    </Card>
  );
};
